import { AccountCategory, activeSourceCurrenciesAtom, sourceCurrenciesAtom } from '@eih-netbroker/common';
import { useAtomValue } from 'jotai/index';
import { lazy, Suspense, useCallback, useContext, useEffect } from 'react';
import classes from '../../../src/modules/core/components/cash-popover/CashPopoverContent.module.scss';
import { useDialog } from '../../context/DialogContext';
import { useLocale } from '../../context/NbLocalizationContext';
import { useSnackbar } from '../../context/SnackbarContext';
import { CurrencyExchangeSourceAndTargetCurrencyManagerContext } from '../../modules/core/components/cash-popover/CurrencyExchangeSourceAndTargetCurrencyManagerContext';
import { NbLoader } from '../components/loader/Loader';
import { useAccounts } from './useAccounts';
import { useCurrencyExchangeModals } from './useCurrencyExchangeModals';
import { useSourceAndTargetCurrencies } from './useSourceAndTargetCurrencies';

const CurrencyExchangeModal = lazy(
  () => import('../../modules/core/components/currency-exchange/CurrencyExchangeModal'),
);

export type CurrencyExchangeDialogConfig = {
  onBeforeOpen?: () => void;
  onInvalidAccount?: () => Promise<void>;
  onError?: () => void;
};

export const useCurrencyExchangeDialog = (config?: CurrencyExchangeDialogConfig) => {
  const { translation } = useLocale();
  const { openDialog } = useDialog();
  const { showError } = useSnackbar();
  const { selectedAccountOrClient, getSelectedAccount } = useAccounts();
  const { openCurrentlyNoExchangeDialog, openNoExchangePeriodDialog } = useCurrencyExchangeModals();
  const { loadSourceCurrencies, startRefreshCurrencies, stopRefreshCurrencies } = useSourceAndTargetCurrencies();
  const sourceCurrencies = useAtomValue(sourceCurrenciesAtom);
  const activeSourceCurrencies = useAtomValue(activeSourceCurrenciesAtom);

  const currencyExchangeSourceAndTargetCurrencyManagerContext = useContext(
    CurrencyExchangeSourceAndTargetCurrencyManagerContext,
  );

  if (!currencyExchangeSourceAndTargetCurrencyManagerContext) {
    throw new Error('CurrencyExchangeSourceAndTargetCurrencyManager context is not provided');
  }

  const { currencyExchangeEvents } = currencyExchangeSourceAndTargetCurrencyManagerContext;

  const invalidAccountSelectedForExchange = useCallback(async () => {
    await getSelectedAccount(false, [AccountCategory.Nyesz]);
  }, [getSelectedAccount]);

  useEffect(() => {
    loadSourceCurrencies().then(() => {
      startRefreshCurrencies();
    });

    return () => {
      stopRefreshCurrencies();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- make sure only loading once
  }, []);

  const openCurrencyExchangeEnabledModal = useCallback(
    async (currency?: string) => {
      await openDialog((close: () => void) => (
        <Suspense
          fallback={
            <div className={classes.loader}>
              <NbLoader
                horizontalCenter
                verticalCenter
              />
            </div>
          }
        >
          <CurrencyExchangeSourceAndTargetCurrencyManagerContext.Provider value={{ currencyExchangeEvents }}>
            <CurrencyExchangeModal
              onCancel={close}
              selectedSourceCurrency={currency || null}
            />
          </CurrencyExchangeSourceAndTargetCurrencyManagerContext.Provider>
        </Suspense>
      ));
    },
    [currencyExchangeEvents, openDialog],
  );

  const handleCurrencyExchangeOpen = useCallback(
    async (currency?: string) => {
      const onBeforeOpen = config?.onBeforeOpen;
      const onInvalidAccount = config?.onInvalidAccount ?? invalidAccountSelectedForExchange;
      const onError = config?.onError;

      onBeforeOpen?.();

      if (!selectedAccountOrClient.account) {
        await onInvalidAccount();
        return;
      }

      if (!activeSourceCurrencies.isLoaded || !sourceCurrencies.isLoaded) {
        showError(translation.general.error.serverError);
        onError?.();
        return;
      }

      if (sourceCurrencies.hasError) {
        showError(translation.general.error.serverError);
        onError?.();
        return;
      }

      try {
        const isEnabled = true;

        if (!isEnabled) {
          await openCurrentlyNoExchangeDialog();
        } else if (!currency) {
          if (activeSourceCurrencies.length > 0) {
            await openCurrencyExchangeEnabledModal();
          } else {
            await openNoExchangePeriodDialog();
          }
        } else if (activeSourceCurrencies.includes(currency)) {
          await openCurrencyExchangeEnabledModal(currency);
        } else {
          await openNoExchangePeriodDialog(currency);
        }
      } catch (err) {
        showError(translation.general.error.serverError);
        onError?.();
      }
    },
    [
      activeSourceCurrencies,
      config?.onBeforeOpen,
      config?.onError,
      config?.onInvalidAccount,
      invalidAccountSelectedForExchange,
      openCurrencyExchangeEnabledModal,
      openCurrentlyNoExchangeDialog,
      openNoExchangePeriodDialog,
      selectedAccountOrClient.account,
      showError,
      sourceCurrencies,
      translation.general.error.serverError,
    ],
  );

  return { handleCurrencyExchangeOpen };
};
